import { MeasurementSystem, SeparatorType } from '@ti/contracts'
import { formatCommaSeparatedNumber } from '@ti/core/common/utils/comma-separated-number'
import {
  FEET_IN_METER,
  FEET_IN_MILE,
  METERS_IN_KILOMETER,
} from '@ti/core/ui-kit/i18n/utils/convert-units'

export type MeasurementKind = 'distance' | 'area'

export interface MeasurementFormatOptions {
  system: MeasurementSystem
  decimalSeparator: SeparatorType
  abbreviate?: boolean
}

export interface FormattedMeasurement {
  text: string
  full?: string
}

const MAX_ABBREVIATED_VALUE = 999

const KINDS: Record<MeasurementKind, { power: number; suffix: string; smallUnitLimit: number }> = {
  distance: { power: 1, suffix: '', smallUnitLimit: 1_000 },
  area: { power: 2, suffix: '²', smallUnitLimit: 1_000_000 },
}

const UNITS: Record<
  MeasurementSystem,
  { small: string; large: string; smallPerMeter: number; smallPerLarge: number }
> = {
  [MeasurementSystem.Metric]: {
    small: 'm',
    large: 'km',
    smallPerMeter: 1,
    smallPerLarge: METERS_IN_KILOMETER,
  },
  [MeasurementSystem.Imperial]: {
    small: 'ft',
    large: 'mi',
    smallPerMeter: FEET_IN_METER,
    smallPerLarge: FEET_IN_MILE,
  },
}

const trimTrailingZeros = (fixed: string) =>
  fixed.includes('.') ? fixed.replace(/\.?0+$/, '') : fixed

export const formatMeasurement = (
  kind: MeasurementKind,
  metricValue: number,
  { system, decimalSeparator, abbreviate = false }: MeasurementFormatOptions,
): FormattedMeasurement | null => {
  if (!Number.isFinite(metricValue) || metricValue < 0) return null

  const { power, suffix, smallUnitLimit } = KINDS[kind]
  const units = UNITS[system]
  const withSeparators = (fixed: string) =>
    formatCommaSeparatedNumber(fixed, decimalSeparator === SeparatorType.Dot ? ',' : '.')

  const smallValue = metricValue * units.smallPerMeter ** power
  const roundedSmallValue = Math.round(smallValue)
  if (roundedSmallValue < smallUnitLimit) {
    return { text: `${withSeparators(String(roundedSmallValue))} ${units.small}${suffix}` }
  }

  const largeValue = smallValue / units.smallPerLarge ** power
  const decimals = largeValue < 10 ? 2 : largeValue < 100 ? 1 : 0
  const fixed = trimTrailingZeros(largeValue.toFixed(decimals))
  const full = `${withSeparators(fixed)} ${units.large}${suffix}`

  return abbreviate && Number(fixed) > MAX_ABBREVIATED_VALUE
    ? { text: `>${MAX_ABBREVIATED_VALUE} ${units.large}${suffix}`, full }
    : { text: full }
}
